/**
 * useTaskDetail.js
 * Logic hook for TaskDetailDialog — state, effects, and API handlers.
 * Stage 2 logic refactor of ActivityDialogs.
 */
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { activityAPI } from '../../../../services/api';
import { associationAPI } from '../../../../services/api';
import api from '../../../../services/api';
import { useAuth } from '../../../../context/AuthContext';
import { useConfirm } from '../../../../components/common/ConfirmDialog';

export function useTaskDetail(task, {
  associations = [],
  users = [],
  communications = [],
  showAccountDropdown = false,
  onTaskUpdate,
  canEditAccount,
  onOpenChange,
  onSuccess,
}) {
  const { user: currentUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const confirm = useConfirm();
  const [saving, setSaving] = useState(false);
  const [showCommDialog, setShowCommDialog] = useState(false);
  const [formData, setFormData] = useState({
    activity_name: '',
    description: '',
    end_date_time: '',
    task_status: 'not_started',
    is_sensitive: false,
    association_id: '',
    horse_id: '',
    linked_account_id: '',
    assigned_to_user_id: '',
    linked_communication_ids: [],
  });
  const [filteredAssociations, setFilteredAssociations] = useState([]);

  // Sync task → formData when task changes
  useEffect(() => {
    if (task) {
      setFormData({
        activity_name: task.activity_name || '',
        description: task.description || '',
        end_date_time: task.end_date_time ? new Date(task.end_date_time).toISOString().split('T')[0] : '',
        task_status: task.task_status || 'not_started',
        is_sensitive: task.is_sensitive || false,
        association_id: task.association_id || '',
        horse_id: task.horse_id || '',
        linked_account_id: task.linked_account_id || task.crm_entity_id || '',
        assigned_to_user_id: task.assigned_to_user_id || '',
        linked_communication_ids: task.linked_communication_ids || [],
      });
    }
  }, [task]);

  // Fetch enriched associations when account changes
  useEffect(() => {
    const fetchAssociationsForAccount = async () => {
      if (showAccountDropdown && formData.linked_account_id) {
        try {
          const response = await associationAPI.list(formData.linked_account_id);
          const list = response.data || [];
          const enriched = await Promise.all(
            list.map(async (assoc) => {
              const otherEntityId = assoc.crm_entity_1_id === formData.linked_account_id
                ? assoc.crm_entity_2_id
                : assoc.crm_entity_1_id;
              try {
                const entityRes = await api.get(`/crm-entities/${otherEntityId}`);
                return { ...assoc, entityName: entityRes.data.system_name };
              } catch {
                return { ...assoc, entityName: 'Unknown Contact' };
              }
            })
          );
          setFilteredAssociations(enriched);
        } catch {
          setFilteredAssociations([]);
        }
      } else if (!showAccountDropdown) {
        setFilteredAssociations(associations);
      } else {
        setFilteredAssociations([]);
      }
    };
    fetchAssociationsForAccount();
  }, [formData.linked_account_id, showAccountDropdown]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    if (!task?.id) return;
    setSaving(true);
    try {
      const dataToSave = { ...formData };
      if (formData.end_date_time) {
        const parts = formData.end_date_time.split('-');
        const dt = new Date(Date.UTC(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]), 12, 0, 0));
        dataToSave.end_date_time = dt.toISOString();
      } else {
        dataToSave.end_date_time = null;
      }
      await activityAPI.update(task.id, dataToSave);
      toast.success('Task updated');
      if (onTaskUpdate) onTaskUpdate({ ...task, ...dataToSave });
      setIsEditing(false);
      onSuccess?.();
    } catch {
      toast.error('Failed to update task');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!task?.id) return;
    if (!await confirm({ title: 'Are you sure you want to delete this task? This will also delete any linked calendar events.', destructive: true })) return;
    setSaving(true);
    try {
      await activityAPI.delete(task.id);
      toast.success('Task deleted');
      onOpenChange(false);
      onSuccess?.();
    } catch {
      toast.error('Failed to delete task');
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    if (task) {
      setFormData({
        activity_name: task.activity_name || '',
        description: task.description || '',
        end_date_time: task.end_date_time ? new Date(task.end_date_time).toISOString().split('T')[0] : '',
        task_status: task.task_status || 'not_started',
        is_sensitive: task.is_sensitive || false,
        association_id: task.association_id || '',
        horse_id: task.horse_id || '',
        linked_account_id: task.linked_account_id || task.crm_entity_id || '',
        assigned_to_user_id: task.assigned_to_user_id || '',
        linked_communication_ids: task.linked_communication_ids || [],
      });
    }
    setIsEditing(false);
  };

  // Derived values
  const canEdit = canEditAccount !== undefined
    ? canEditAccount
    : (task && (task.created_by_id === currentUser?.id
        || currentUser?.roles?.includes('tenant_system_admin')
        || currentUser?.roles?.includes('system_owner')));
  const linkedAssoc   = associations.find(a => a.id === task?.association_id);
  const assignedUser  = users.find(u => u.id === task?.assigned_to_user_id);
  const linkedComms   = communications.filter(c => task?.linked_communication_ids?.includes(c.id));

  return {
    isEditing, setIsEditing,
    saving,
    showCommDialog, setShowCommDialog,
    formData, setFormData,
    filteredAssociations,
    handleSave, handleDelete, cancelEdit,
    canEdit, linkedAssoc, assignedUser, linkedComms,
  };
}
